import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  Button,
  FlexColumn,
  FlexRow,
  SvgIcon,
  TableRow,
  TableRowType
} from '@make-software/cspr-design';
import Table from 'app/components/common/table/table';
import BaseTableHeader from 'app/components/common/base-table/base-table-header';
import { ErrorResult, Pagination } from 'app/organizations-api/types';
import { getOffers, PaginationResponseType } from 'app/console-api';
import { MyOffersListItem } from 'app/components/management/apps/offers/list/my-offers-list-item';
import {
  APPS_OFFERS_CREATE_PATH,
  APPS_OFFERS_EDIT_PATH,
  APPS_OFFERS_VIEW_PATH,
  APPS_STATISTICS_PATH,
  SIGN_IN_PATH
} from 'app/route/paths';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { HttpError } from 'app/components/utils/http-error';
import { AUTH_TOKEN, customErrorMessage, statusCode } from 'app/constants';
import { PreloadTableWindow } from 'app/components/common/preload-table-window/preload-table-window';
import NoFound from 'assets/logos/no-found.svg';
import { ExtendedOffersResponse } from 'app/console-api/offers/offers-extended';
import { StyledTableFiltersBlock, TablePageContainer } from 'app/components/common/common-styles';
import { IsActiveAdapter, IsActiveFilter } from 'app/components/filters/is-active-filter';
import { TaskTypeAdapter, TaskTypeFilter } from 'app/components/filters/task-type-filter';
import { DealTypesAdapter, DealTypesFilter } from 'app/components/filters/deal-types-filter';
import { SkeletonLoader } from 'app/components/common/skeleton-loader/skeleton-loader';
import { useUrlFilterController } from 'app/components/filters/url-filters';
import { EntityType } from 'app/console-api/plugins/plugins';

const StyledButton = styled(Button)(({ theme }) =>
  theme.withMedia({
    width: '130px',
    padding: ['0', 'unset', 'unset']
  })
);

export const MyOffersList = () => {
  const { appID: appID } = useParams<{ appID: string }>();
  const [loading, setLoading] = useState<boolean>(true);
  const [pagination, setPagination] = useState<Pagination>({ currentPage: 1, pageSize: 5 });
  const [unexpectedError, setUnexpectedError] = useState<ErrorResult | null>(null);
  const filterController = useUrlFilterController();
  const [offersList, setOffersList] =
    useState<PaginationResponseType<ExtendedOffersResponse> | null>(null);
  const navigate = useNavigate();
  const lastFetchedFiltersKeyRef = React.useRef<string | null>(null);

  useEffect(() => {
    setLoading(true);
    if (!appID) {
      return;
    }
    const filtersKey = filterController.buildCacheKey([IsActiveAdapter, TaskTypeAdapter, DealTypesAdapter]);
    if (lastFetchedFiltersKeyRef.current !== filtersKey) {
      setPagination((p) => (p.currentPage === 1 ? p : { ...p, currentPage: 1 }));
      pagination.currentPage = 1;
    }
    lastFetchedFiltersKeyRef.current = filtersKey;

    getOffers(appID, filterController, pagination)
      .then((response) => {
        setOffersList(response);
      })
      .catch((error: any) => {
        if (error instanceof HttpError) {
          switch (error.statusCode) {
            case statusCode.validation_error:
              setUnexpectedError({
                code: error.statusCode.toString(),
                message: error.message
              });
              break;
            case statusCode.authentication_error:
              localStorage.removeItem(AUTH_TOKEN);
              window.location.href = SIGN_IN_PATH;
              return;
            case statusCode.unexpected_error:
              setUnexpectedError(customErrorMessage);
              break;
            default:
              setUnexpectedError(customErrorMessage);
              break;
          }
        } else {
          setUnexpectedError(customErrorMessage);
        }
        console.error('Failed to fetch offer:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [appID, filterController, pagination]);

  const viewOffer = (data: ExtendedOffersResponse) => {
    navigate(generatePath(APPS_OFFERS_VIEW_PATH, { appID: appID!, id: data.offer.id }));
  };

  const editOffer = (data: ExtendedOffersResponse) => {
    navigate(generatePath(APPS_OFFERS_EDIT_PATH, { appID: appID!, id: data.offer.id }));
  };

  const handlePageActions = (page: number, limit: number) => {
    if (pagination.pageSize != limit) {
      page = 1;
    }
    setPagination((prev) => ({
      ...prev,
      currentPage: page,
      pageSize: limit
    }));
  };

  const createButton = () => {
    navigate(generatePath(APPS_OFFERS_CREATE_PATH, { appID: appID! }));
  };

  const moreStatisticsLink = (id: string) => {
    navigate(
      generatePath(APPS_STATISTICS_PATH, { appID: appID || '' }) +
        '?' +
        new URLSearchParams({ offers: id }).toString()
    );
  };

  const noOffer = !offersList?.data?.length && !loading && !unexpectedError;

  return (
    <TablePageContainer>
      <FlexColumn>
        <FlexRow justify={'space-between'} align={'center'} style={{ marginRight: '12px' }}>
          <StyledTableFiltersBlock>
            {appID ? (
              <>
                <IsActiveFilter />
                <TaskTypeFilter eventType={EntityType.Deal} />
                <DealTypesFilter />
              </>
            ) : (
              <SkeletonLoader height={'35px'} />
            )}
          </StyledTableFiltersBlock>
          <FlexColumn>
            <StyledButton color="primaryBlue" onClick={createButton}>
              Create new
            </StyledButton>
          </FlexColumn>
        </FlexRow>
        {noOffer ? (
          <PreloadTableWindow
            icon={<SvgIcon src={NoFound} height={120} width={224} />}
            title="No offers yet"
            windowTitle=""
            subtitle="Create one to start deal"
            onSubmitTitle="Create new"
            onSubmit={createButton}
          />
        ) : (
          <Table
            loading={loading}
            data={offersList?.data || []}
            pageCount={offersList?.page_count || 1}
            error={unexpectedError}
            pageSize={pagination.pageSize}
            currentPage={pagination.currentPage}
            handlePageActions={handlePageActions}
            tableRowType={TableRowType.TextSingleLine}
            renderDataHeader={() => (
              <TableRow>
                <BaseTableHeader fixedWidthRem={25}>Offer</BaseTableHeader>
                <BaseTableHeader fixedWidthRem={20}>Details</BaseTableHeader>
                <BaseTableHeader fixedWidthRem={20}></BaseTableHeader>
                <BaseTableHeader fixedWidthRem={25}>Stats</BaseTableHeader>
                <BaseTableHeader align="right">Action</BaseTableHeader>
              </TableRow>
            )}
            renderPaginatedData={(one) => {
              return one.map((offer) => (
                <MyOffersListItem
                  key={offer.offer.id}
                  offer={offer}
                  onView={viewOffer}
                  onEdit={editOffer}
                  onStatistics={moreStatisticsLink}
                />
              ));
            }}
          />
        )}
      </FlexColumn>
    </TablePageContainer>
  );
};
